import { cn } from '../lib/cn';
import type { Verdict } from '../lib/types';

/**
 * What a badge can show. `REJECTED` is not a fourth verdict — it is the absence
 * of one. The gate refused the reading, so nothing was ever judged and the check
 * is still owed. It is red for the same reason OUT is red (both mean "this is not
 * done and somebody has to act"), and it carries a DASHED border so the two are
 * distinguishable at a glance without spending a second colour on it.
 */
export type VerdictBadgeState = Verdict | 'REJECTED' | CofaVerdict | CofaResultVerdict;

/** A certificate of analysis as judged against the supplier's spec when it was recorded, and
 *  each result on it (migration 014). A recommendation: a person decides. ⚠ These mirror
 *  CK_CofA_Verdict and CK_CofAResult_Verdict (rule 14, vocabulary.test.ts). */
export type CofaVerdict = 'PASS' | 'FAIL' | 'NEEDS_REVIEW' | 'NOT_ASSESSED';
/** INFO (migration 027): printed, not in our spec - shown, never judged. */
export type CofaResultVerdict = 'IN' | 'OUT' | 'NEEDS_REVIEW' | 'MISSING' | 'INFO';

interface VerdictBadgeProps {
  /** Null renders a neutral placeholder rather than nothing, so a table row does
   *  not change height the moment a result arrives. */
  verdict: VerdictBadgeState | null;
  /** `lg` is the line: the operator reads it from arm's length at a docked iPad.
   *  `sm` is the office table. */
  size?: 'sm' | 'lg';
  className?: string;
  /** A labelled, quieter form: "Results: needs review" as coloured words, no pill. (UI/UX audit,
   *  31 Oct 2026, UX-23) On the Goods in register the certificate's verdict sat as a second pill
   *  under the state pill, and "Needs review" beside "Awaiting decision" read as a second task. It
   *  is a recommendation about the RESULTS, not a step, so there it is said as one. The word and its
   *  colour are unchanged; only the pill is dropped. */
  label?: string;
}

/* The triads come from index.css and flip with the theme. Note there is no
   `text-red-600` anywhere: Magic Red exists in this app as `out` and nowhere
   else, and this component is one of the only two places it is spent. */
const TONE: Record<VerdictBadgeState, string> = {
  IN: 'bg-in-bg text-in-fg border-in-line',
  ACTION: 'bg-action-bg text-action-fg border-action-line',
  OUT: 'bg-out-bg text-out-fg border-out-line',
  REJECTED: 'bg-out-bg text-out-fg border-out-line border-dashed',
  // A certificate's: PASS is IN's colour, FAIL is OUT's, a question for a person is ACTION's.
  PASS: 'bg-in-bg text-in-fg border-in-line',
  FAIL: 'bg-out-bg text-out-fg border-out-line',
  NEEDS_REVIEW: 'bg-action-bg text-action-fg border-action-line',
  // Absences are dashed, as REJECTED is: nothing to judge it against; a check not reported.
  NOT_ASSESSED: 'border-hairline bg-elevated text-ink-faint border-dashed',
  MISSING: 'bg-action-bg text-action-fg border-action-line border-dashed',
  // Printed, not in our spec: nothing to act on, so no verdict colour is spent on it.
  INFO: 'border-hairline bg-elevated text-ink-faint',
};

/** Words, not codes, in sentence case like every other pill in the app (Amit, 27 Oct 2026: "one caps
 *  and not the other"). "NEEDS_REVIEW" read aloud is not a sentence. */
const LABEL: Record<VerdictBadgeState, string> = {
  IN: 'In',
  ACTION: 'Action',
  OUT: 'Out',
  REJECTED: 'Rejected',
  PASS: 'Pass',
  FAIL: 'Fail',
  NEEDS_REVIEW: 'Needs review',
  NOT_ASSESSED: 'Not assessed',
  MISSING: 'Missing',
  INFO: 'Not judged',
};

// 14px floor, even in the dense office table. index.css reserves 12px for units
// and column labels and says so explicitly: never a value, a verdict or a reject
// reason. A verdict at 12px is the one thing on the row somebody is scanning for.
const SIZE = {
  // `sm` is the office's pill shape (goodsInModel's chipClass), so a verdict and a state chip read as one set.
  sm: 'rounded-(--radius-pill) px-s2 py-[2px] text-body',
  lg: 'rounded px-s3 py-s2 text-lg',
} as const;

/** The same signal as words alone, for the labelled form (UX-23): each badge's text colour, no fill. */
const QUIET_TONE: Record<VerdictBadgeState, string> = {
  IN: 'text-in-fg',
  ACTION: 'text-action-fg',
  OUT: 'text-out-fg',
  REJECTED: 'text-out-fg',
  PASS: 'text-in-fg',
  FAIL: 'text-out-fg',
  NEEDS_REVIEW: 'text-action-fg',
  NOT_ASSESSED: 'text-ink-faint',
  MISSING: 'text-action-fg',
  INFO: 'text-ink-faint',
};

export function VerdictBadge({ verdict, size = 'sm', className, label }: VerdictBadgeProps) {
  const base = 'inline-flex items-center border font-semibold';

  if (label !== undefined && verdict !== null) {
    return (
      <span className={cn('text-body', className)}>
        <span className="text-ink-faint">{label}: </span>
        <span className={cn('font-semibold', QUIET_TONE[verdict])}>{LABEL[verdict].toLowerCase()}</span>
      </span>
    );
  }

  if (verdict === null) {
    return (
      <span
        className={cn(base, SIZE[size], 'border-hairline bg-elevated text-ink-faint', className)}
        title="No result yet"
      >
        —
      </span>
    );
  }

  return (
    // The word IS the signal; the colour only reinforces it. A badge that said
    // nothing and relied on red alone would be unreadable to a colour-blind
    // operator and invisible in a printed or photographed record — and these
    // screens get photographed.
    <span className={cn(base, SIZE[size], TONE[verdict], className)}>{LABEL[verdict]}</span>
  );
}

// Exported both ways. The named export is the one to use; the default exists so
// an `import VerdictBadge from` written out of habit resolves rather than failing
// the build in a file nobody is editing at the time.
export default VerdictBadge;
